/**
 * NPCsView — DM-facing tab for the per-campaign NPC roster.
 *
 * Phase WS-5 — first user-visible surface for the world-state system. Lists
 * accepted NPCs as a card grid; pending NPCs (proposed by world-extraction
 * during session analysis) get a distinct dashed-orange treatment so the DM
 * knows what needs review. Click a card → side drawer with full profile,
 * Edit / Accept / Delete actions.
 *
 * Permission gating: writes go through the backend's canActOnWorldEntity
 * check, which is DM- or org-admin-only. The UI shows the buttons to
 * everyone and surfaces 403s as Mantine notifications — same pattern as the
 * canon journal/chronicle review pane. Cleaner than client-side role checks
 * that would drift out of sync with backend rules.
 *
 * This file is the smart wrapper: the four CRUD mutations come from the shared
 * useWorldEntityMutations factory; it adds the list fetch, the awaitable
 * handler callbacks the dumb view needs to close its modals/drawer, and the
 * two bulk (Promise.all) helpers that stay ad-hoc to avoid N notifications.
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  acceptNpc,
  createNpc,
  deleteNpc,
  fetchNpcs,
  type INpcCreateBody,
  type INpcEditBody,
  updateNpc,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import { useWorldEntityMutations } from "../WorldEntityShell/useWorldEntityMutations"
import type { IProps } from "./interfaces"
import { NPCsViewView } from "./NPCsViewView"

export const NPCsView = ({ seriesId, initialEntityId, initialCreateName }: IProps): React.JSX.Element => {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const npcsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldNpcs(org, seriesId),
    queryFn: (client) => fetchNpcs(client, seriesId),
  })

  const { createMutation, updateMutation, acceptMutation, deleteMutation } = useWorldEntityMutations<
    INpcCreateBody,
    INpcEditBody
  >({
    seriesId,
    queryKey: (org) => queryKeys.worldNpcs(org, seriesId),
    create: createNpc,
    update: updateNpc,
    accept: acceptNpc,
    remove: deleteNpc,
    labels: {
      created: msg`NPC created`,
      saved: msg`NPC saved`,
      accepted: msg`NPC accepted`,
      deleted: msg`NPC deleted`,
    },
  })

  const handleCreate = useCallback(
    async (body: INpcCreateBody): Promise<void> => {
      await createMutation.mutateAsync(body)
    },
    [createMutation],
  )

  const handleUpdate = useCallback(
    async (npcId: string, patch: INpcEditBody): Promise<void> => {
      await updateMutation.mutateAsync({ id: npcId, patch })
    },
    [updateMutation],
  )

  const handleAccept = useCallback(
    async (npcId: string): Promise<void> => {
      await acceptMutation.mutateAsync(npcId)
    },
    [acceptMutation],
  )

  const handleDelete = useCallback(
    async (npcId: string): Promise<void> => {
      await deleteMutation.mutateAsync(npcId)
    },
    [deleteMutation],
  )

  const handleAcceptAllPending = useCallback(
    async (npcIds: string[]): Promise<void> => {
      if (!company || npcIds.length === 0) return
      try {
        await Promise.all(npcIds.map((id) => acceptNpc(company, seriesId, id)))
        notifications.show({ message: i18n._(msg`Accepted ${npcIds.length} NPCs`), color: "success" })
        queryClient.invalidateQueries({ queryKey: queryKeys.worldNpcs(orgId, seriesId) })
      } catch (err) {
        notifications.show({ message: extractErrorMessage(err), color: "danger" })
      }
    },
    [company, seriesId, queryClient, i18n, orgId],
  )

  const handleDeleteAllPending = useCallback(
    async (npcIds: string[]): Promise<void> => {
      if (!company || npcIds.length === 0) return
      try {
        await Promise.all(npcIds.map((id) => deleteNpc(company, seriesId, id)))
        notifications.show({ message: i18n._(msg`Deleted ${npcIds.length} pending NPCs`), color: "success" })
        queryClient.invalidateQueries({ queryKey: queryKeys.worldNpcs(orgId, seriesId) })
      } catch (err) {
        notifications.show({ message: extractErrorMessage(err), color: "danger" })
      }
    },
    [company, seriesId, queryClient, i18n, orgId],
  )

  return (
    <NPCsViewView
      npcs={npcsQuery.data ?? []}
      isLoading={npcsQuery.isLoading}
      initialEntityId={initialEntityId}
      initialCreateName={initialCreateName}
      isCreating={createMutation.isPending}
      isUpdating={updateMutation.isPending}
      isAccepting={acceptMutation.isPending}
      isDeleting={deleteMutation.isPending}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onAccept={handleAccept}
      onDelete={handleDelete}
      onAcceptAllPending={handleAcceptAllPending}
      onDeleteAllPending={handleDeleteAllPending}
    />
  )
}
